import { type SkuId, type GetFavouriteSKUsResponse, type GetSKUsResponse, type StoreName } from '@bluebrick/discounts-service-module'
import { type SnakeCasedPropertiesDeep } from 'type-fest'
import { convertToSnakeCase } from '@/common/utils'
import { useApi } from '@/features/api/useAPI'
import { useNotificationsStore } from '@/features/notifications/useNotificationsStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

export async function fetchAllSKUs(storeNames: StoreName[]): Promise<SnakeCasedPropertiesDeep<GetSKUsResponse>> {
	const { notifyError } = useNotificationsStore()
	const { trackEvent, trackException } = useTelemetry()
	const { apiGet } = useApi()

	if (!storeNames || storeNames.length === 0) {
		trackEvent('app', 'No SKUs to fetch')

		return {}
	}

	try {
		const { data } = await apiGet<GetSKUsResponse>(`/events/skus`, {
			params: {
				storeNames
			}
		})

		trackEvent('app', 'Fetched all SKUs', {
			toValue: JSON.stringify(
				Object.entries(data).reduce<Partial<Record<StoreName, number>>>((result, [storeName, skus]) => {
					result[<StoreName>storeName] = skus?.length ?? 0

					return result
				}, {})
			)
		})

		/* TODO: For consitency with current state and to avoid mapping stuff back and forth we convert returned
		data to snake_case. Case handling in entire app will be refactored by https://indiebi.atlassian.net/browse/DA-339
		*/
		return convertToSnakeCase(data)
	} catch (e) {
		trackException(e as Error, 'Failed to load SKUs.')

		notifyError('Failed to load SKUs.')

		return {}
	}
}

export async function fetchFavouriteSKUIds(): Promise<string[]> {
	const { notifyError } = useNotificationsStore()
	const { trackEvent, trackException } = useTelemetry()
	const { apiGet } = useApi()

	try {
		const { data } = await apiGet<GetFavouriteSKUsResponse>('/events/skus/favourites')

		trackEvent('app', 'Fetched favourite SKU ids', {
			count: data?.uniqueSKUIds?.length
		})

		return data?.uniqueSKUIds ?? []
	} catch (e) {
		trackException(e as Error, 'Failed to load favourite SKUs.')

		notifyError('Failed to load favourite SKUs.')

		return []
	}
}

export async function updateFavouriteSKUIds(skuIds: SkuId[]): Promise<string[]> {
	const { notifyError } = useNotificationsStore()
	const { trackEvent, trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		const { data } = await apiPost<GetFavouriteSKUsResponse>('/events/skus/favourites', {
			uniqueSKUIds: skuIds
		})

		trackEvent('app', 'Updated favourite SKU ids', {
			count: data?.uniqueSKUIds?.length
		})

		return data?.uniqueSKUIds ?? []
	} catch (e) {
		trackException(e as Error, 'Failed to save favourite SKUs.')

		notifyError('Failed to save favourite SKUs.')

		return skuIds
	}
}
